import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { ComparisonRes, compareArrays, wrapAsArray } from "@cervval.js/common/src/tools/collection.array.tools";
import { RowUid } from "@cervval.js/common/src/ui/columnConfiguration/columnConfiguration";
import { CjsSmartTable, RowGlobalIndex, SmartTableCell } from "../smartTable";
import { SmartTableCheckboxSelectorColumn } from "../smartTableCheckboxSelectorColumn";
import { SELECTION_TYPE } from "./smartTableSelectionModel.types";
export * from "./smartTableSelectionModel.types";


/**
 * Event emitted when the value of a MatSelectionModel has changed.
 */
export interface SelectionChange<ItemData> {
    /** Model that dispatched the event. */
    source: SmartTableSelectionModel<ItemData>;
    /** Row uids that were added to the model. */
    added: RowUid[];
    /** Row uids that were removed from the model. */
    removed: RowUid[];

    /** New selection of table */
    newSelection: RowUid[];

    /** Old selection of table */
    oldSelection: RowUid[];
}

export interface SmartTableSelectOptions {
    /**
     * true if shift is handled
     * Used to select intervals
     */
    isShift: boolean;
    /**
     * true if ctrl is handled
     * Used to select multiple rows
     */
    isCtrl: boolean;

    /**
     * if true, a selected row will not be unselected if row is markSelected, default toggle behavior is disabled
     */
    doNotToggleSelection?: boolean;

    /**
     * if true, leadSelectionRow will not be updated by given row
     */
    doNotUpdateLeadSelectionRow?: boolean;

    /**
     * If true, change event will not be dispatched,
     * and prevented event data will be passed in next fired event.
     * If you want to also prevent data propagation, you can use clearSelectionQueue to do so.
     */
    doNotDispatchChangeEvent?: boolean;
}

/**
 * Selection model for smartTable
 */
export class SmartTableSelectionModel<ItemData> {

    /** Event dispatched when selection has changed */
    @Event()
    public readonly selectionChangedEvent!: EventHandler<SelectionChange<ItemData>>;

    /**
     * Currently-selected values.
     * Internal representation of current selection
     */
    private _selection = new Set<RowUid>();

    /** Type of selection */
    public get type(): SELECTION_TYPE {
        return this.smartTable.options.selection?.selectionType ?? SELECTION_TYPE.SINGLE;
    }

    /** Keeps track of the previous selection that haven't been emitted by the change event. */
    private _previousSelectionToCompareWhenEmitSet: Set<RowUid> | undefined;

    /**
     * Lead Selection row : most recent row selected by user
     */
    public leadSelectionRow: RowUid = "";

    /**
     * Constructor, default selection type is single
     */
    constructor(
        public smartTable: CjsSmartTable<ItemData>
    ) { }

    /**
     * @returns current selection
     */
    public getSelection(): RowUid[] {
        return Array.from(this._selection.values());
    }

    /**
     * @returns current selection with rows global index
     */
    public getSelectionIndexes(): RowGlobalIndex[] {
        const selectedIndexes: RowGlobalIndex[] = [];

        for (const rowUid of this._selection) {
            const row = this.smartTable.rowsByUid.get(rowUid);
            if (row) { selectedIndexes.push(row.globalIndex); }
        }

        return selectedIndexes;
    }

    /**
     * Is rowUid given selected in selection list
     */
    public isSelected(rowUid: RowUid): boolean {
        return this._selection.has(rowUid);
    }

    /**
     * Selects a row or an array of rows.
     * @param rowUids The values to select
     * @returns Whether the selection changed as a result of this call
     */
    public select(rowUids: (RowUid | RowUid[]), options?: SmartTableSelectOptions): boolean {
        // Save previous selection
        this._saveSelection();

        const uids = wrapAsArray(rowUids);

        // Select given rows
        uids.forEach(rowUid => this._markSelected(rowUid, options));
        const changed = this._hasQueuedChanges();

        if (changed) {
            // Update selection state in rows objects
            this.refreshRowsSelectionInSmartTable();
        }

        if (!options?.doNotDispatchChangeEvent) {
            this._emitSelectionChangedEvent();
        }

        return changed;
    }

    /**
     * Deselects a value or an array of values.
     * @param rowUids The values to deselect
     * @returns Whether the selection changed as a result of this call
     */
    public deselect(...rowUids: RowUid[]): boolean {
        // Save previous selection
        this._saveSelection();

        rowUids.forEach(row => this._unmarkSelected(row));
        const changed = this._hasQueuedChanges();

        if (changed) {
            // Update selection state in rows objects
            this.refreshRowsSelectionInSmartTable();
        }

        this._emitSelectionChangedEvent();
        return changed;
    }

    /**
     * Sets the selected rows uids
     * @param rowUids The new selected values
     * @returns Whether the selection changed as a result of this call
     */
    public setSelection(rowUids: (RowUid | RowUid[]), options?: SmartTableSelectOptions): boolean {

        // Store old values
        const oldValues = this.getSelection();
        // Clear previous selection
        this.clear({ doNotDispatchEvent: true, doNotRefreshSmartTable: true });

        // Select given rows
        const uids = wrapAsArray(rowUids);
        uids.forEach(row => this._markSelected(row, options));
        const newSelectedSet = new Set(this.getSelection());

        // Add removed rows to deselect list for event
        oldValues
            .filter(rowUid => !newSelectedSet.has(rowUid))
            .forEach(rowUid => this._unmarkSelected(rowUid));

        const changed = this._hasQueuedChanges();
        if (changed) {
            // Update selection state in rows objects
            this.refreshRowsSelectionInSmartTable();
        }

        if (!options?.doNotDispatchChangeEvent) {
            this._emitSelectionChangedEvent();
        }

        return changed;
    }


    /** Clear selection / deselection row uid caches
     * By default, selection change event fires only what changed since previous change event, so there is no need to use this function.
     * When knowingly preventing said event emission, changes will stack until next fired event. To prevent stacking you can clean selection caches.
     */
    public clearSelectionQueue(): void {
        this._previousSelectionToCompareWhenEmitSet = undefined;
    }

    /** Selects a value */
    private _markSelected(rowUid: RowUid, options?: SmartTableSelectOptions): void {
        const row = this.smartTable.rowsByUid.get(rowUid);

        // Can't mark selected a row that isn't in smartTable or isn't selectable
        if (!row || !this.smartTable.isSelectableRow(row)) { return; }

        if (!row.displayed) { return; }


        const isShift = Boolean(options?.isShift);
        let isCtrl = Boolean(options?.isCtrl);

        // Can't re-select same row in single mode
        if (this.isSingle() && this.isSelected(rowUid)) {
            return;
        }

        // In multiple mode, always add clicked row to selection, same behavior as ctrl key pressed
        if (this.isMultiple()) { isCtrl = true; }

        // If multiple interval, when a row is clicked and already selected, unselect it
        if (!options?.doNotToggleSelection && isCtrl && (this.isMultipleInterval() || this.isMultiple()) && this.isSelected(rowUid)) {
            this._unmarkSelected(rowUid);
            return;
        }

        // SINGLE
        // For single selection type, unselect all others rows before selecting this row
        if (this.isSingle()) {
            this.leadSelectionRow = rowUid;
            this._unmarkAll();
        }

        // SINGLE INTERVAL
        if (this.isSingleInterval()) {
            if (isShift) {
                // Get rows between given rowUid and leadSelection row
                const rows = this._getRowsBetweenRowAndLeadSelection(rowUid);
                // Mark selected all of theses rows
                rows.forEach(rUid => this._markSelected(rUid, {
                    isCtrl: false,
                    isShift: false,
                    doNotToggleSelection: true,// Force select, don't deselect if row is already selected
                    doNotUpdateLeadSelectionRow: true// Don't update leadSelection row, should not change
                }));

                // if no value already selected, leadSelection row became clicked row
                if (!this.hasValue() && !options?.doNotUpdateLeadSelectionRow) {
                    this.leadSelectionRow = rowUid;
                }

            } else {// Default behavior
                if (!options?.doNotUpdateLeadSelectionRow) {
                    this.leadSelectionRow = rowUid;// Update lead selection Row
                    this._unmarkAll();// Unmark all other rows, only one row can be selected without shift
                }
            }
        }

        // MULTIPLE INTERVAL
        if (this.isMultipleInterval() || this.isMultiple()) {
            if (isShift) {
                // Get rows between given rowUid and leadSelection row
                const rows = this._getRowsBetweenRowAndLeadSelection(rowUid);
                // Mark selected all of theses rows
                rows.forEach(rUid => {
                    this._markSelected(rUid, {
                        isCtrl: false,
                        isShift: false,
                        doNotToggleSelection: true,// Force select, don't unselect if row is already selected
                        doNotUpdateLeadSelectionRow: true// Don't update leadSelection row, should not change
                    });
                });

                // if no value already selected, leadSelection row became clicked row
                if (!this.hasValue() && !options?.doNotUpdateLeadSelectionRow) {
                    this.leadSelectionRow = rowUid;
                }

            } else {// Default behavior
                if (!options?.doNotUpdateLeadSelectionRow) {
                    this.leadSelectionRow = rowUid;// Update lead selection Row
                    if (!isCtrl) {// Is user don't handle ctrl, we should select only clicked row => unmark all other rows
                        this._unmarkAll();
                    }
                }
            }
        }

        // Select clicked row if not already selected
        if (!this.isSelected(rowUid)) {
            this._selection.add(rowUid);

            // Select all children of row to select
            // Check if smartTable have some hierarchy parent rows, to avoid useless compute
            if (
                this.smartTable.options.selection?.hierarchyParentSelectionWillSelectChildren &&
                this.smartTable.hierarchyHelper.parent.size &&
                !this.isSingle()
            ) {// Can't mark children in single mode
                // Retrieve children of selected row
                const children = this.smartTable.hierarchyHelper.children.get(row) ?? [];

                // Select all children
                children.forEach(child => {
                    this._markSelected(child.uid, { isCtrl: false, isShift: false, doNotToggleSelection: true, doNotUpdateLeadSelectionRow: true });
                });
            }
        }

    }

    /** Deselects a value. */
    private _unmarkSelected(rowUid: RowUid): void {

        // Unselect all children of row to unselect
        // Check if smartTable have some hierarchy parent rows, to avoid useless compute
        if (
            this.smartTable.options.selection?.hierarchyParentSelectionWillSelectChildren &&
            this.smartTable.hierarchyHelper.parent.size &&
            !this.isSingle()// Can't mark children in single mode
        ) {

            const row = this.smartTable.rowsByUid.get(rowUid);

            // Retrieve children of row to unselect
            const children = row ? this.smartTable.hierarchyHelper.children.get(row) ?? [] : [];

            if (row && !row.displayed) { return; }

            // UnSelect all children
            children.forEach(child => {
                this._unmarkSelected(child.uid);
            });
        }

        this._selection.delete(rowUid);
    }

    /** Clears out the selected values. */
    private _unmarkAll(): void {
        if (this.hasValue()) {
            this._selection.forEach(rowUid => this._unmarkSelected(rowUid));
        }
    }

    /**
     * Refresh row selection state from current selection model
     */
    private _refreshRowsSelectionFromSelectionModel(): void {

        // Update selected state
        for (const row of this.smartTable.tableRows) {
            row.selected = this.isSelected(row.uid);
        }

        // Update footer number of displayable rows if td cell element exists
        if (this.smartTable.options.enableFooterInfo?.selectedRowsCount) {
            this.smartTable.styleHelper.refreshFooterInfoDisplay();
        }
    }

    /** Refresh rows selection in smartTable: Apply selection model into smartTable */
    public refreshRowsSelectionInSmartTable(): void {
        // Update selection state in rows objects
        this._refreshRowsSelectionFromSelectionModel();
        this.smartTable.styleHelper.refreshRowsSelectionDisplay();
    }

    /**
     * Determines whether the model does not have a value.
     */
    public isEmpty(): boolean {
        return this._selection.size === 0;
    }

    /**
     * Determines whether the model has a value.
     */
    public hasValue(): boolean {
        return !this.isEmpty();
    }

    /**
     * @returns Lead Selection row global index
     */
    public getLeadSelectionGlobalIndex(options?: { globalIndexByRowUid: GlobalIndexByRowUidMap }): RowGlobalIndex {
        const globalIndexByRowUid = options?.globalIndexByRowUid ?? this.getRowUidGlobalIndexMaps().globalIndexByRowUid;
        return globalIndexByRowUid.get(this.leadSelectionRow) ?? -1;
    }

    /**
     * Compare previousSelection array with current selection array and returns true if equal
     * @param previousSelection Array of indexes to compare with the current selection
     */
    public selectionHasChanged(previousSelection: RowUid[]): boolean {
        return previousSelection.length !== this._selection.size || previousSelection.some(value => !this._selection.has(value));
    }

    /** Emits a change event and clears the records of selected and deselected values. */
    private _emitSelectionChangedEvent(options?: {
        doNotDispatchEvent?: boolean
    }): void {

        if (!options?.doNotDispatchEvent) {// Don't compute useless code if event don't need to be dispatched

            const selectionCompare = this._getSelectionCompare();
            const [toAdd, toRemove] = [selectionCompare.toAdd, selectionCompare.toRemove];

            // Selection has changed only if some items to add or remove
            const selectionHasChanged = Boolean(toAdd.length || toRemove.length);

            // Dispatch change event only if selection has changed with new selection
            if (selectionHasChanged) {
                this.selectionChangedEvent.emit({
                    source: this,
                    added: toAdd,
                    removed: toRemove,
                    newSelection: [...this._selection.values()],
                    oldSelection: [...this._previousSelectionToCompareWhenEmitSet?.values() ?? []]
                });
            }
        }

        this.clearSelectionQueue();
    }

    /**
     * Clears all of the selected values.
     * @param p
     * @param p.doNotDispatchEvent Whether to flush the changes in an event.
     * @param p.doNotRefreshSmartTable Whether to don't refresh smartTable, event if selection has changed
     * If false, the changes to the selection will be flushed along with the next event.
     * @returns Whether the selection changed as a result of this call
     */
    public clear(p?: { doNotDispatchEvent?: boolean, doNotRefreshSmartTable?: boolean }): boolean {

        // Save previous selection
        this._saveSelection();

        // Unmark all selection
        this._unmarkAll();

        const changed = this._hasQueuedChanges();

        if (changed && !p?.doNotRefreshSmartTable) {
            this.refreshRowsSelectionInSmartTable();
        }

        if (!p?.doNotDispatchEvent) {
            this._emitSelectionChangedEvent();
        }

        return changed;
    }

    /** Whether there are queued up change to be emitted. */
    private _hasQueuedChanges(): boolean {
        const { toAdd, toRemove } = this._getSelectionCompare();
        return Boolean(toAdd.length || toRemove.length);
    }

    /** Save current selection to compare changes between emits */
    private _saveSelection(): void {
        this._previousSelectionToCompareWhenEmitSet = new Set([...this._selection.values()]);
    }

    /** Get result of comparison between old selection and new selection */
    private _getSelectionCompare(): ComparisonRes<RowUid> {
        return compareArrays({
            oldArray: [...this._previousSelectionToCompareWhenEmitSet?.values() ?? []],
            newArray: [...this._selection.values()],
            matchingElementsFct: (uid1, uid2) => uid1 === uid2
        });
    }
    /** 
     * Behavior : enable selection on row click, except when checkbox column have "rowSelectionWithCheckboxOnly" property
     * In this case, allow selection only when clicking on said checkbox (not on row)
     */
    public handleRowSelectionWithCheckboxOnly(cell: SmartTableCell<ItemData>, event?: MouseEvent | TouchEvent): { selectionHasChanged: boolean, handled: boolean } {
        if (!event) { return { selectionHasChanged: false, handled: false }; }

        const rowUid = cell.row.uid;

        // Retrieve checkbox column if exist
        const checkboxColumn = this.smartTable.options.columns.find(c => c instanceof SmartTableCheckboxSelectorColumn) as SmartTableCheckboxSelectorColumn<ItemData> | undefined;
        // Checkbox of column
        const checkboxCell = checkboxColumn?.checkboxesByRow[rowUid];
        // True if click target is checkbox
        const isCheckboxClicked = event?.target === checkboxCell?.components.formCheckInput;

        const handled = Boolean(checkboxColumn?.rowSelectionWithCheckboxOnly && !isCheckboxClicked);

        return { selectionHasChanged: false, handled };
    }

    /**
     * Returns array that contains uids of rows between given rowUid and leadSelectionRow
     * @param rowUid row to retrieve index
     * @returns RowUid[]
     */
    public _getRowsBetweenRowAndLeadSelection(rowUid: RowUid): RowUid[] {

        const rows = this.smartTable.displayableRows;

        // Search leadSelectionRow and given row indexes inside smartTableRows to define interval
        let leadSelectionRowIndex = -1;
        let rowIndex = -1;
        for (let i = 0; i < rows.length; i++) {// Only one loop for perfs
            const row = rows[i];
            if (row.uid === this.leadSelectionRow) { leadSelectionRowIndex = i; }
            if (row.uid === rowUid) { rowIndex = i; }
            if (rowIndex !== -1 && leadSelectionRowIndex !== -1) {
                break;// Both index found, useless to continue loop
            }
        }

        // Order indexes
        let startIndex = rowIndex;
        let finishIndex = leadSelectionRowIndex;
        if (startIndex > finishIndex) {
            [startIndex, finishIndex] = [finishIndex, startIndex];
        }

        // Ensure startIndex is always defined - could return '-1' when leadSelectionRow isn't defined
        if (startIndex < 0 || startIndex == null) { startIndex = 0; }

        return rows.slice(startIndex, finishIndex + 1).map(r => r.uid);// Add "+1" to include finishIndex in slice method
    }

    /** @returns true if selection type is single row */
    public isSingle(): boolean {
        return this.type === SELECTION_TYPE.SINGLE;
    }

    /** @returns true if selection type is single interval of rows */
    public isSingleInterval(): boolean {
        return this.type === SELECTION_TYPE.SINGLE_INTERVAL;
    }

    /** @returns true if selection type is multiple rows */
    public isMultiple(): boolean {
        return this.type === SELECTION_TYPE.MULTIPLE;
    }

    /** @returns true if selection type is multiple intervals of rows */
    public isMultipleInterval(): boolean {
        return this.type === SELECTION_TYPE.MULTIPLE_INTERVAL;
    }

    /** Build RowUid<>GlobalIndex associative maps */
    public getRowUidGlobalIndexMaps(): SmartTableSelectionModelMaps {
        const rowUidMap: RowUidByGlobalIndexMap = new Map();
        const globalIndexMap: GlobalIndexByRowUidMap = new Map();

        for (const row of this.smartTable.tableRows) {
            rowUidMap.set(row.globalIndex, row.uid);
            globalIndexMap.set(row.uid, row.globalIndex);
        }

        return {
            rowUidByGlobalIndex: rowUidMap,
            globalIndexByRowUid: globalIndexMap
        };
    }
}


/** Type <GlobalIndex, RowUid> map */
export type RowUidByGlobalIndexMap = Map<RowGlobalIndex, RowUid>;
/** Type <RowUid, GlobalIndex> map */
export type GlobalIndexByRowUidMap = Map<RowUid, RowGlobalIndex>;

/** Store RowUid and GlobalIndex associative maps */
export interface SmartTableSelectionModelMaps {
    /** Store <GlobalIndex, RowUid> map */
    rowUidByGlobalIndex: RowUidByGlobalIndexMap;
    /** Store <RowUid, GlobalIndex> map */
    globalIndexByRowUid: GlobalIndexByRowUidMap;
}